import { useEffect } from "react";
import { useSelector } from "react-redux";
import { Route, Routes, useNavigate, useParams } from "react-router-dom";

import { Form, FormikErrors } from "formik";

import { FormPagination, FormStatus } from "components/form";
import {
  ImageUploaderContext,
  ImageUploaderProvider,
  setUrlsInValues,
} from "components/form/fields";
import { TabContent } from "components/layout";
import { getActiveOrganizationSelector } from "containers/Auth/selectors";
import { RestrictedArea, ROLES } from "containers/Permissions";
import {
  TiVoImage,
  useCreateProvider,
  useGenerateImageUpload,
  usePatchProvider,
  useProvider,
  useUpdateProvider,
  useUpdateProviderPresentations,
} from "hooks/tivoApps";
import { getCountries } from "utils/continents";
import { pathToUrl } from "utils/url";
import { isTabInvalid } from "utils/validation";

import { PATHS } from "../constants";
import { IMAGE_SCHEMA, PROVIDER_TABS, TEXTS } from "./constants";
import { PROVIDER_FIELDS } from "./fields";
import { TiVoAppFormik } from "./TiVoAppFormik";
import {
  createTabLink,
  getActiveTab,
  TiVoAppFormTabs,
} from "./TiVoAppFormTabs";
import { ProviderForm } from "./types";
import {
  adaptProviderFormToRequest,
  getProviderFormInitialValues,
  useFormMode,
  adaptPresentationToRequest,
  adaptProviderFormToPatch,
} from "./utils";
import { createProviderValidationSchema } from "./validation";

export function TiVoProviderForm() {
  const mode = useFormMode();
  const navigate = useNavigate();
  const params = useParams();
  const userRoles = useSelector(getActiveOrganizationSelector)?.roles ?? [];
  const isBizDevOnly =
    userRoles.includes(ROLES.tivo.bizDev) &&
    !userRoles.includes(ROLES.tivo.provider);
  const {
    data: providerData,
    isLoading: providerLoading,
    error: providerError,
  } = useProvider(params.id, { include: "presentations" });
  const countries = getCountries();
  const createProvider = useCreateProvider();
  const updateProvider = useUpdateProvider(params.id);
  const patchProvider = usePatchProvider(params.id);
  const updateProviderPresentations = useUpdateProviderPresentations();
  const { mutateAsync: generateImageUpload } = useGenerateImageUpload();

  const allowedTabs = PROVIDER_TABS.filter(({ allowed }) =>
    allowed.some((role) => userRoles.includes(role))
  );

  const handleSubmit = async (
    values: ProviderForm,
    uploadImages: (
      imageExtra: Partial<TiVoImage>
    ) => Promise<{ name: string; url: string }[]>
  ) => {
    const onSuccess = ({ results }: { results: { id: string } }) => {
      navigate(
        pathToUrl(PATHS.detailsProvider, {
          id: results.id,
          tabId: "",
        })
      );
    };

    const buildProviderData = async () => {
      const urls = await uploadImages({ providerId: values.providerId });
      return adaptProviderFormToRequest(
        setUrlsInValues(urls, values),
        countries
      );
    };
    const presentations = values.presentations.map(adaptPresentationToRequest);

    if (mode === "createProvider") {
      const data = await buildProviderData();
      const response = await createProvider.mutateAsync(data);
      await updateProviderPresentations.mutateAsync({
        presentations,
        id: response.results.id,
        isCreateMode: true,
      });
      return onSuccess(response);
    }

    if (isBizDevOnly) {
      const response = await patchProvider.mutateAsync(
        adaptProviderFormToPatch(values)
      );
      return onSuccess(response);
    }

    const data = await buildProviderData();
    await updateProviderPresentations.mutateAsync({
      presentations,
      id: params.id!,
    });
    const response = await updateProvider.mutateAsync(data);
    return onSuccess(response);
  };

  const activeTab = getActiveTab(PROVIDER_TABS, params);

  // Non-existing tab should redirect to 404
  useEffect(() => {
    if (
      Boolean(params["*"]) &&
      !PROVIDER_TABS.some((tab) => tab.path === params["*"])
    ) {
      navigate("/404");
    }
  }, [navigate, params]);

  // Launch without tab should redirect to first available tab
  useEffect(() => {
    if (!allowedTabs.length || Boolean(params["*"])) {
      return;
    }

    navigate(
      createTabLink({
        id: params.id,
        mode,
        tabId: allowedTabs[0].path,
      }),
      { replace: true }
    );
  }, [allowedTabs, mode, navigate, params, params.id]);

  const pageHeader =
    mode === "createProvider"
      ? "Create new provider"
      : providerData?.results.label || "Edit provider";

  const redirectToInvalidTab = (errors: FormikErrors<ProviderForm>) => {
    const firstInvalidTab = allowedTabs.find(({ fields }) =>
      isTabInvalid(fields, errors)
    );

    if (!firstInvalidTab) {
      return;
    }

    navigate(
      createTabLink({
        id: params.id,
        mode,
        tabId: firstInvalidTab.path,
      })
    );
  };
  return (
    <ImageUploaderProvider
      generateImageUpload={generateImageUpload}
      imageSchema={IMAGE_SCHEMA}
    >
      <ImageUploaderContext.Consumer>
        {({ uploadImages }) => (
          <TiVoAppFormik
            header={pageHeader}
            loading={providerLoading}
            error={providerError}
            onSubmit={(values) =>
              handleSubmit(values, uploadImages.mutateAsync)
            }
            initialValues={getProviderFormInitialValues(
              providerData?.results,
              countries
            )}
            validationSchema={createProviderValidationSchema(PROVIDER_FIELDS)}
          >
            {({ handleReset, handleSubmit, validateForm }) => {
              return (
                <>
                  <TiVoAppFormTabs tabs={PROVIDER_TABS} />
                  <TabContent>
                    <Form>
                      <Routes>
                        {PROVIDER_TABS.map(({ Component, path, allowed }) => (
                          <Route
                            key={path}
                            path={path}
                            element={
                              <RestrictedArea allowed={allowed}>
                                <Component />
                              </RestrictedArea>
                            }
                          />
                        ))}
                      </Routes>
                    </Form>
                  </TabContent>
                  <FormStatus
                    submitMutations={[
                      createProvider,
                      updateProvider,
                      patchProvider,
                      uploadImages,
                      updateProviderPresentations,
                    ]}
                    withErrorDetails
                  />
                  <FormPagination
                    activeTab={activeTab}
                    discardLabel={TEXTS.SHARED.DISCARD_BUTTON_LABEL}
                    submitLabel={TEXTS.SHARED.SAVE_BUTTON_LABEL}
                    tabCount={allowedTabs.length}
                    tabTitle={PROVIDER_TABS[activeTab]?.text}
                    onDiscard={handleReset}
                    onSubmit={async () => {
                      const errors = await validateForm();
                      redirectToInvalidTab(errors);
                      handleSubmit();
                    }}
                    saving={
                      createProvider.isPending ||
                      updateProvider.isPending ||
                      patchProvider.isPending ||
                      uploadImages.isPending
                    }
                  />
                </>
              );
            }}
          </TiVoAppFormik>
        )}
      </ImageUploaderContext.Consumer>
    </ImageUploaderProvider>
  );
}
